import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { createContext, useContext, useState, type ReactNode } from 'react';
import { useAbility } from '@/auth/ability-provider';

/**
 * Two ways to use this module:
 *
 *  1. Standalone <MaskedAmount value="₨ 1,000" /> — each instance carries its
 *     own eye icon and reveal state. Fine when a single sensitive number sits
 *     by itself on the page.
 *
 *  2. Grouped — wrap a card in <RevealGroup>...</RevealGroup>. Every
 *     MaskedAmount inside follows the group's reveal state and renders text
 *     only (no per-instance eye). Place <RevealToggle /> wherever you want
 *     the single button to appear (typically the card header).
 *
 * Auth gate (`can('reveal','FinancialAmount')`) applies in both modes — users
 * without the capability see permanent dots and a disabled icon.
 */

interface RevealCtx {
  revealed: boolean;
  toggle: () => void;
  allowed: boolean;
}
const RevealContext = createContext<RevealCtx | null>(null);

/** Wrap a card / section so every MaskedAmount inside shares one toggle. */
export function RevealGroup({
  children,
  defaultRevealed = false,
}: { children: ReactNode; defaultRevealed?: boolean }) {
  const ability = useAbility();
  const allowed = ability.can('reveal', 'FinancialAmount');
  const [revealed, setRevealed] = useState(defaultRevealed && allowed);
  return (
    <RevealContext.Provider
      value={{ revealed, toggle: () => setRevealed((v) => !v), allowed }}
    >
      {children}
    </RevealContext.Provider>
  );
}

/** Drop inside a RevealGroup wherever the single eye icon should appear. */
export function RevealToggle({ style }: { style?: React.CSSProperties }) {
  const ctx = useContext(RevealContext);
  if (!ctx) {
    // Render nothing in dev if used outside a RevealGroup — silent fail keeps
    // the page from crashing if someone forgets the wrapper.
    return null;
  }
  if (!ctx.allowed) {
    return (
      <Tooltip title="You don't have permission to view amounts on this card">
        <EyeInvisibleOutlined
          style={{ opacity: 0.4, cursor: 'not-allowed', ...style }}
        />
      </Tooltip>
    );
  }
  return (
    <Tooltip title={ctx.revealed ? 'Hide amounts' : 'Show amounts'}>
      <button
        type="button"
        onClick={ctx.toggle}
        aria-label={ctx.revealed ? 'Hide amounts' : 'Show amounts'}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          color: 'inherit',
          opacity: 0.65,
          display: 'inline-flex',
          ...style,
        }}
      >
        {ctx.revealed ? <EyeOutlined /> : <EyeInvisibleOutlined />}
      </button>
    </Tooltip>
  );
}

interface Props {
  /** Pre-formatted display string (e.g. "₨ 1,42,500"). Required so the component stays formatting-agnostic. */
  value: ReactNode;
  /** Width of the dot placeholder when masked. Defaults to 6 dots. */
  maskWidth?: number;
  /** Initial visibility — only honoured in standalone mode. */
  defaultRevealed?: boolean;
  /**
   * Override the reveal gate. By default uses `ability.can('reveal','FinancialAmount')`.
   * Pass `true` to always allow reveal, `false` to permanently mask.
   * Ignored in grouped mode (group's allowed state wins).
   */
  canReveal?: boolean;
  /** Optional inline style for the rendered span. */
  style?: React.CSSProperties;
}

export function MaskedAmount({
  value,
  maskWidth = 6,
  defaultRevealed = false,
  canReveal,
  style,
}: Props) {
  const ctx = useContext(RevealContext);
  // Grouped mode — text-only, no own button. Group's RevealToggle is the only UI.
  if (ctx) {
    const dots = '•'.repeat(maskWidth);
    return (
      <span style={{ letterSpacing: ctx.revealed && ctx.allowed ? 0 : 1, ...style }}>
        {ctx.revealed && ctx.allowed ? value : dots}
      </span>
    );
  }
  return (
    <StandaloneMaskedAmount
      value={value}
      maskWidth={maskWidth}
      defaultRevealed={defaultRevealed}
      canReveal={canReveal}
      style={style}
    />
  );
}

function StandaloneMaskedAmount({
  value,
  maskWidth = 6,
  defaultRevealed = false,
  canReveal,
  style,
}: Props) {
  const ability = useAbility();
  const allowed = canReveal ?? ability.can('reveal', 'FinancialAmount');
  const [revealed, setRevealed] = useState(defaultRevealed && allowed);

  const dots = '•'.repeat(maskWidth);

  if (!allowed) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, ...style }}>
        <span style={{ letterSpacing: 1 }}>{dots}</span>
        <Tooltip title="You don't have permission to view this amount">
          <EyeInvisibleOutlined style={{ opacity: 0.4, cursor: 'not-allowed' }} />
        </Tooltip>
      </span>
    );
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, ...style }}>
      <span style={{ letterSpacing: revealed ? 0 : 1 }}>{revealed ? value : dots}</span>
      <Tooltip title={revealed ? 'Hide' : 'Show amount'}>
        <button
          type="button"
          onClick={() => setRevealed((v) => !v)}
          aria-label={revealed ? 'Hide amount' : 'Show amount'}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            color: 'inherit',
            opacity: 0.65,
            display: 'inline-flex',
          }}
        >
          {revealed ? <EyeOutlined /> : <EyeInvisibleOutlined />}
        </button>
      </Tooltip>
    </span>
  );
}
